/**
 * `/leads/:id/selection` — the Selection builder (spec §Web → Staff, plan
 * Task 14). Prefills from the lead's `LeadItem`s, adds more through the
 * catalogue picker, and is the only pricing surface — `$leadId.edit.tsx` is
 * deferred. Online-only by design: a Selection link is allocated
 * server-side and useless without a network, so this never touches Dexie,
 * the outbox or the sync engine (spec §H).
 *
 * `?edit=<selectionId>` reopens an existing Selection instead of starting a
 * new one, and saving PATCHes it in place. The token survives, so a link
 * already with a buyer shows the edit — which is why this exists rather than
 * the plan's original revoke-and-recreate, where fixing a typo meant sending
 * a second link and hoping they opened the right one.
 *
 * VOCABULARY (2026-08-07): every string on this screen says "catalogue", which
 * is what the office calls the thing it sends. The model, route and types stay
 * `Selection` because `catalogue` already names the PRODUCT catalogue in code —
 * see the note on `model Selection` in schema.prisma. A product's own price is
 * "list price" in copy, so "catalogue" means exactly one thing to a reader.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, Link, useBlocker } from '@tanstack/react-router';
import {
  applyPercentage,
  CreatedSelection,
  CreateSelectionInput,
  MAX_SELECTION_ITEMS,
  normalizeMoneyInput,
  selectionDocRef,
  UpdateSelectionInput,
  type PriceMode,
  type ProductWithPhotos
} from '@paradise/shared';
import { z } from 'zod';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { detailQueryKey } from '@/components/lead-detail/use-lead-detail';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { CataloguePickerSheet } from '@/components/selection-builder/catalogue-picker-sheet';
import { PricingRow } from '@/components/selection-builder/pricing-row';
import { SelectionItemRow } from '@/components/selection-builder/selection-item-row';
import { useLeadQuoteQuery } from '@/components/print/use-lead-quote-query';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiRequest, describeApiFailure } from '@/lib/api/http';
import { useSessionStore } from '@/lib/auth/session';
import { useCatalogueProducts } from '@/lib/catalogue-data';
import { useIsOffline } from '@/lib/offline';
import {
  buyerGreetingName,
  mailtoShareHref,
  shareMessage,
  whatsappNumber,
  whatsappShareHref
} from '@/lib/share-message';

export const Route = createFileRoute('/_app/leads/$leadId/selection')({
  staticData: { hideShellChrome: true },
  validateSearch: z.object({ edit: z.string().optional() }),
  component: SelectionBuilderRoute
});

interface BuilderItem {
  productId: string;
  /** Display fallback for a product that has since dropped out of the catalogue mirror. */
  productNameSnapshot: string;
  /**
   * The catalogue price as it read when this line was added — captured, not re-read.
   * It is the markup base, the figure shown beside the editable price, and what the
   * server stores as `listPriceSnapshot`; a background mirror refresh mid-edit would
   * otherwise let those three drift and make the saved margin wrong.
   */
  listPriceSnapshot: string | null;
  unitPriceText: string;
}

/** Marked-up prices land on a multiple of ₹5. Fixed, not a control — the picker was cut 2026-08-04. */
const ROUNDING_STEP_RUPEES = 5;

/** Everything a save would send, as one comparable string — the unsaved-changes test. */
function builderSignature(items: readonly BuilderItem[], priceMode: PriceMode): string {
  return `${priceMode}|${items.map(item => `${item.productId}:${item.unitPriceText}`).join(',')}`;
}

const PRICE_MODE_HINT: Record<PriceMode, string> = {
  NONE: 'No prices reach the buyer — just photos, descriptions and MOQ.',
  EXACT: 'The buyer sees the exact price on every line.',
  BAND: 'The buyer sees a range (e.g. "₹750 – ₹1,500") instead of the figure.'
};

const STATUS_MESSAGE: Record<'not-found' | 'offline' | 'unauthenticated' | 'error', string> = {
  'not-found': 'This lead could not be found.',
  offline: 'Offline — building a catalogue needs a connection.',
  unauthenticated: 'Signed out — sign in again to build a catalogue.',
  error: 'Could not load this lead. Try again in a moment.'
};

function SelectionBuilderRoute() {
  const { leadId } = Route.useParams();
  const { edit: editId } = Route.useSearch();
  const isEdit = editId !== undefined;
  const isOffline = useIsOffline();
  const queryClient = useQueryClient();
  /** Signs the share message — the buyer should know which of us sent the link. */
  const senderName = useSessionStore(state => state.user?.name ?? null);

  const { data: result, isPending } = useLeadQuoteQuery(leadId);
  // The Selection being edited. `enabled` keeps a fresh build from fetching
  // anything; `staleTime: Infinity` keeps a background refetch from stamping
  // over prices staff are part-way through typing.
  const existingQuery = useQuery({
    queryKey: ['selection', editId],
    enabled: editId !== undefined,
    staleTime: Infinity,
    queryFn: async () => {
      const outcome = await apiRequest<CreatedSelection>(`/api/selections/${editId ?? ''}`, {
        schema: CreatedSelection
      });
      if (outcome.kind !== 'ok') throw new Error(describeApiFailure(outcome));
      return outcome.data;
    }
  });
  const existing = existingQuery.data;
  const catalogueProducts = useCatalogueProducts();
  const productById = useMemo(() => new Map(catalogueProducts.map(p => [p.id, p] as const)), [catalogueProducts]);

  const [items, setItems] = useState<BuilderItem[]>([]);
  const [priceMode, setPriceMode] = useState<PriceMode>('NONE');
  const [hasHandEdits, setHasHandEdits] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedSelection | null>(null);
  const [copied, setCopied] = useState(false);

  // Seed once, and only once the catalogue mirror has actually resolved —
  // `useCatalogueProducts` starts at `[]` before Dexie answers, and seeding
  // against that would give every line a blank catalogue price.
  const seededForRef = useRef<string | null>(null);
  /** The signature at seed time — what "unchanged" means for this build. */
  const baselineRef = useRef<string | null>(null);
  const seedKey = editId ?? leadId;

  // Editing: the saved lines are the truth, prices included. `unitPrice` is
  // null under NONE, so an edit that switches to EXACT falls back to the
  // stored catalogue snapshot rather than to a blank field.
  useEffect(() => {
    if (existing === undefined) return;
    if (seededForRef.current === seedKey) return;
    seededForRef.current = seedKey;
    const seeded = existing.items.map(item => ({
      productId: item.productId,
      productNameSnapshot: item.productNameSnapshot,
      listPriceSnapshot: item.listPriceSnapshot,
      unitPriceText: item.unitPrice ?? item.listPriceSnapshot ?? ''
    }));
    setPriceMode(existing.priceMode);
    setItems(seeded);
    baselineRef.current = builderSignature(seeded, existing.priceMode);
  }, [existing, seedKey]);

  useEffect(() => {
    if (editId !== undefined) return;
    if (!result || result.status !== 'ok' || !result.lead) return;
    if (productById.size === 0) return;
    if (seededForRef.current === seedKey) return;
    seededForRef.current = seedKey;
    const seeded = result.lead.items.map(item => {
      const product = productById.get(item.productId);
      // Starting price is the CATALOGUE price, never the booth-quoted
      // LeadItem.quotedPrice — that figure is deliberately outside the
      // Product.listPrice → SelectionItem.unitPrice chain (spec → Pricing
      // a Selection).
      return {
        productId: item.productId,
        productNameSnapshot: product?.name ?? item.productNameSnapshot,
        listPriceSnapshot: product?.listPrice ?? null,
        unitPriceText: product?.listPrice ?? ''
      };
    });
    setItems(seeded);
    baselineRef.current = builderSignature(seeded, 'NONE');
  }, [result, editId, seedKey, productById]);

  function toBuilderItem(product: ProductWithPhotos): BuilderItem {
    return {
      productId: product.id,
      productNameSnapshot: product.name,
      listPriceSnapshot: product.listPrice ?? null,
      unitPriceText: product.listPrice ?? ''
    };
  }

  function addProduct(product: ProductWithPhotos): void {
    setItems(prev =>
      prev.some(item => item.productId === product.id) || prev.length >= MAX_SELECTION_ITEMS
        ? prev
        : [...prev, toBuilderItem(product)]
    );
  }

  function addProducts(products: readonly ProductWithPhotos[]): void {
    setItems(prev => {
      const have = new Set(prev.map(item => item.productId));
      const fresh = products.filter(product => !have.has(product.id)).slice(0, MAX_SELECTION_ITEMS - prev.length);
      return fresh.length === 0 ? prev : [...prev, ...fresh.map(toBuilderItem)];
    });
  }

  function removeItem(productId: string): void {
    setItems(prev => prev.filter(item => item.productId !== productId));
  }

  function setPrice(productId: string, text: string): void {
    setHasHandEdits(true);
    setItems(prev => prev.map(item => (item.productId === productId ? { ...item, unitPriceText: text } : item)));
  }

  // Recomputes every line from the catalogue price — never from the current
  // `unitPriceText` — so re-applying does not compound (spec §Verification 9).
  function applyBulkPercentage(percent: number): void {
    const bps = percent * 100;
    setItems(prev =>
      prev.map(item => {
        if (item.listPriceSnapshot === null) return item; // nothing to mark up against
        return { ...item, unitPriceText: applyPercentage(item.listPriceSnapshot, bps, ROUNDING_STEP_RUPEES) };
      })
    );
    setHasHandEdits(false);
  }

  const selectedIds = useMemo(() => new Set(items.map(item => item.productId)), [items]);
  const allLinesPriced = items.every(item => normalizeMoneyInput(item.unitPriceText) !== null);
  // Compared against the seeded baseline rather than "has any items": opening
  // a Selection and closing it untouched must neither offer a pointless save
  // nor argue with you on the way out.
  const isDirty = baselineRef.current !== null && baselineRef.current !== builderSignature(items, priceMode);
  // The picker stops at the cap, but the prefill from the lead does not — a
  // buyer who was shown more products than a catalogue holds seeds an
  // over-cap build, which the schema would reject at save time.
  const overCapBy = Math.max(0, items.length - MAX_SELECTION_ITEMS);
  const canSave =
    !isOffline &&
    !saving &&
    items.length > 0 &&
    overCapBy === 0 &&
    (priceMode === 'NONE' || allLinesPriced) &&
    result?.status === 'ok' &&
    (!isEdit || isDirty);

  async function handleSave(): Promise<void> {
    if (!result || result.status !== 'ok' || !result.lead) return;
    const wireItems = items.map((item, index) => ({
      productId: item.productId,
      listPriceSnapshot: item.listPriceSnapshot,
      unitPrice: normalizeMoneyInput(item.unitPriceText),
      sortOrder: index
    }));

    const parsed =
      editId === undefined
        ? CreateSelectionInput.safeParse({ leadId: result.lead.id, priceMode, items: wireItems })
        : UpdateSelectionInput.safeParse({ priceMode, items: wireItems });
    if (!parsed.success) {
      setSaveError(
        overCapBy > 0
          ? `A catalogue holds ${String(MAX_SELECTION_ITEMS)} products — remove ${String(overCapBy)} to save this one.`
          : `Check the form — ${parsed.error.issues[0]?.message ?? 'something is not valid yet'}.`
      );
      return;
    }

    setSaving(true);
    setSaveError(null);
    const outcome = await apiRequest<CreatedSelection>(
      editId === undefined ? `/api/leads/${result.lead.id}/selections` : `/api/selections/${editId}`,
      { method: editId === undefined ? 'POST' : 'PATCH', body: parsed.data, schema: CreatedSelection }
    );
    setSaving(false);
    if (outcome.kind === 'ok') {
      // The lead screen lists Selections with their item counts — stale the
      // moment this returns.
      await queryClient.invalidateQueries({ queryKey: detailQueryKey(leadId) });
      if (editId !== undefined) queryClient.setQueryData(['selection', editId], outcome.data);
      setCreated(outcome.data);
      return;
    }
    setSaveError(
      describeApiFailure(outcome, {
        offline: `Lost connection while ${editId === undefined ? 'creating' : 'saving'} the catalogue. Try again once you are back online.`
      })
    );
  }

  async function copyLink(shareUrl: string): Promise<void> {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
  }

  // Hand-priced lines are typed one at a time and exist nowhere else until
  // save — this screen is deliberately outside the outbox (spec §H), so a
  // stray back gesture is unrecoverable. Same pattern as the product form.
  const blocker = useBlocker({
    shouldBlockFn: () => isDirty && created === null && !saving,
    withResolver: true
  });

  if (isPending || !result || (editId !== undefined && existingQuery.isPending)) {
    return (
      <div className="grid h-dvh place-items-center text-sm text-muted-foreground">
        <span className="flex items-center gap-2">
          <Spinner className="size-4" aria-hidden="true" /> Loading…
        </span>
      </div>
    );
  }

  // `/leads/:leadId/selection?edit=:id` carries two independently addressable
  // ids, and `GET /api/selections/:id` resolves the Selection on its own. A
  // hand-edited or stale URL that pairs them wrongly would otherwise show one
  // buyer's products under another buyer's name — and save them there.
  const belongsToLead = existing === undefined || existing.leadId === leadId;

  if (editId !== undefined && (existingQuery.isError || !belongsToLead)) {
    return (
      <div className="grid h-dvh place-items-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            {belongsToLead ? 'This catalogue could not be opened.' : 'This catalogue belongs to a different buyer.'}
          </p>
          <Button render={<Link to="/leads/$leadId" params={{ leadId }} />} variant="outline" size="sm">
            ‹ Back to buyer
          </Button>
        </div>
      </div>
    );
  }

  if (result.status !== 'ok' || !result.lead) {
    return (
      <div className="grid h-dvh place-items-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            {STATUS_MESSAGE[result.status === 'ok' ? 'error' : result.status]}
          </p>
          <Button render={<Link to="/leads/$leadId" params={{ leadId }} />} variant="outline" size="sm">
            ‹ Back to buyer
          </Button>
        </div>
      </div>
    );
  }

  const lead = result.lead;

  if (created) {
    const shareUrl = created.shareUrl;
    return (
      <div className="mx-auto flex max-w-lg flex-col gap-4 p-6">
        <h1 className="font-heading text-xl font-semibold">{isEdit ? 'Catalogue saved' : 'Catalogue created'}</h1>
        <p className="text-sm text-muted-foreground">
          Ref <span className="font-medium text-foreground">{selectionDocRef(created.id)}</span> —{' '}
          {isEdit
            ? `the link ${lead.company} already has now shows these ${String(created.items.length)} products.`
            : `send this link to ${lead.company}.`}
        </p>
        <div className="rounded-lg border bg-muted/40 p-3 text-sm break-all">{shareUrl}</div>
        <div className="flex flex-wrap gap-2">
          <Button
            render={
              <a
                href={whatsappShareHref(
                  shareMessage({
                    kind: 'catalogue',
                    buyerName: buyerGreetingName(lead.contactName, lead.company),
                    senderName,
                    shareUrl
                  }),
                  whatsappNumber(lead.phone, lead.country)
                )}
                target="_blank"
                rel="noopener noreferrer"
              />
            }
          >
            Send on WhatsApp
          </Button>
          <Button
            render={
              <a
                href={mailtoShareHref(
                  `Your selection from The Paradise (${selectionDocRef(created.id)})`,
                  shareMessage({
                    kind: 'catalogue',
                    buyerName: buyerGreetingName(lead.contactName, lead.company),
                    senderName,
                    shareUrl
                  }),
                  lead.email
                )}
              />
            }
            variant="secondary"
          >
            Send by email
          </Button>
          <Button type="button" variant="secondary" onClick={() => void copyLink(shareUrl)}>
            {copied ? 'Copied ✓' : 'Copy link'}
          </Button>
          {/* Opening the buyer's own page is the only way to check what they
              will actually see — price mode, bands and all. */}
          <Button
            render={<a href={shareUrl} target="_blank" rel="noopener noreferrer" />}
            variant="outline"
            title="Opens the buyer's page in a new tab"
          >
            Open buyer view
          </Button>
          <Button render={<Link to="/leads/$leadId" params={{ leadId }} />} variant="outline">
            Back to buyer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-24">
      <header className="flex flex-none items-center gap-2.5 border-b bg-card px-4 py-3">
        <Button render={<Link to="/leads/$leadId" params={{ leadId }} />} variant="ghost" size="sm" className="-ml-1.5">
          ‹ {lead.company}
        </Button>
        <div className="font-heading mr-auto truncate text-lg font-semibold">
          {isEdit ? `Edit catalogue ${selectionDocRef(editId)}` : 'Build a catalogue'}
        </div>
      </header>

      {isEdit && (
        <p className="-mb-1 px-4 text-xs text-muted-foreground">
          Saving updates the link {lead.company} already has. Nothing new is sent.
        </p>
      )}

      <div className="flex flex-col gap-4 px-4">
        <div className="flex flex-col gap-1.5">
          <Tabs
            value={priceMode}
            onValueChange={(value: string) => {
              if (value === 'NONE' || value === 'EXACT' || value === 'BAND') setPriceMode(value);
            }}
          >
            <TabsList>
              <TabsTrigger value="NONE">No prices</TabsTrigger>
              <TabsTrigger value="EXACT">Exact prices</TabsTrigger>
              <TabsTrigger value="BAND">Price bands</TabsTrigger>
            </TabsList>
          </Tabs>
          <p className="text-[11px] text-muted-foreground">{PRICE_MODE_HINT[priceMode]}</p>
        </div>

        <PricingRow itemCount={items.length} hasHandEdits={hasHandEdits} onApply={applyBulkPercentage} />

        <div className="flex items-center gap-2">
          <div className="text-xs font-semibold tracking-wider text-primary uppercase">
            Catalogue · {items.length} item{items.length === 1 ? '' : 's'}
          </div>
          <Button type="button" variant="secondary" size="sm" className="ml-auto" onClick={() => setPickerOpen(true)}>
            + Add products
          </Button>
        </div>

        {items.length === 0 ? (
          <Empty className="flex-none border">
            <EmptyHeader>
              <EmptyDescription>
                No products yet — tap <strong className="font-medium text-foreground">+ Add products</strong>.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="flex flex-col gap-2.5">
            {items.map(item => (
              <SelectionItemRow
                key={item.productId}
                productId={item.productId}
                productNameSnapshot={item.productNameSnapshot}
                listPriceSnapshot={item.listPriceSnapshot}
                unitPriceText={item.unitPriceText}
                priceMode={priceMode}
                product={productById.get(item.productId)}
                onPriceChange={text => setPrice(item.productId, text)}
                onRemove={() => removeItem(item.productId)}
              />
            ))}
          </div>
        )}

        {!allLinesPriced && priceMode !== 'NONE' && items.length > 0 && (
          <p className="text-xs text-destructive">
            Every line needs a price under {priceMode === 'EXACT' ? 'Exact prices' : 'Price bands'} — apply a markup or
            fill in the blanks.
          </p>
        )}
        {overCapBy > 0 && (
          <p className="text-xs text-destructive">
            A catalogue holds {MAX_SELECTION_ITEMS} products — this build has {items.length}. Remove {overCapBy} to
            save.
          </p>
        )}
        {saveError && <p className="text-xs text-destructive">{saveError}</p>}
      </div>

      {/* Sticky, like the capture form's footer. A hundred priced lines can sit
          above this, and the Save that mints the buyer's link used to be at the
          very bottom of that scroll. */}
      <footer className="fixed inset-x-0 bottom-0 z-20 flex justify-center border-t bg-card px-4 py-3">
        <div className="flex w-full max-w-[660px] items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {items.length} item{items.length === 1 ? '' : 's'}
          </span>
          {/* The button names the outcome, and the outcome differs: a new build
              mints a link to send, an edit changes one the buyer already holds. */}
          <Button type="button" disabled={!canSave} onClick={() => void handleSave()} className="ml-auto">
            {saving ? (isEdit ? 'Saving…' : 'Creating…') : isEdit ? 'Save changes' : 'Create catalogue'}
          </Button>
        </div>
      </footer>

      <CataloguePickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        selectedIds={selectedIds}
        onAdd={addProduct}
        onRemove={removeItem}
        onAddMany={addProducts}
      />

      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onOpenChange={open => {
          if (!open) blocker.reset?.();
        }}
        title="Leave without saving this catalogue?"
        description="The products and prices on this screen have not been saved, and leaving loses them."
        confirmLabel="Leave"
        destructive
        onConfirm={() => blocker.proceed?.()}
      />
    </div>
  );
}
